import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import {
    CompactListColumn,
    CompactListItem,
    Vehicle, VehicleActivityTypeHelper,
    VehicleBlockActivityInfo
} from 'src/app/modules/shared/models';

import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import moment from 'moment';
import { BladeService } from 'src/app/modules/blades/services/blade.service';
import { ColumnFactory } from 'src/app/modules/shared/helpers/column-factory';
import { TranslateModule } from '@ngx-translate/core';
import { FeedbackTextComponent } from '../../../modules/shared/components/feedback-text/feedback-text.component';
import { CompactListComponent } from '../../../modules/shared/components/compact-list/compact-list.component';
import { SpinnerComponent } from '../../../modules/shared/components/spinner/spinner.component';


@Component({
    selector: 'app-vehicle-block-info',
    templateUrl: './vehicle-block-info.component.html',
    styleUrls: ['./vehicle-block-info.component.scss'],
    imports: [SpinnerComponent, CompactListComponent, FeedbackTextComponent, TranslateModule],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class VehicleBlockInfoComponent implements OnInit, OnDestroy, OnChanges {
    private _router = inject(Router);
    private _route = inject(ActivatedRoute);
    private _bladeService = inject(BladeService);

    @Input() vehicleBlockActivities: VehicleBlockActivityInfo[];
    @Input() vehicle: Vehicle;
    @Input() showCurrentAndNearestFuture = false;
    @Input() drillInOutlet = 'outlet2';

    public columns: CompactListColumn[] = [];
    public items: CompactListItem[];

    private _ngUnsubscribe$ = new Subject<void>();


    constructor() { }

    ngOnInit(): void {
        this._initColumnDefaults();

        this._bladeService.allOutletParams$.pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe(params => {
            if (!params) return;
            const vehicleActivityId = params['vehicleActivityId'];

            this._resetSelection();

            if (vehicleActivityId) {
                this._selectItem(vehicleActivityId);
            }
        });

    }

    ngOnDestroy() {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }

    ngOnChanges(simpleChanges: SimpleChanges) {
        if (simpleChanges.vehicleBlockActivities || simpleChanges.vehicle) {
            this._updateItems();

            if (this.showCurrentAndNearestFuture) {
                this._showOnlyCurrentAndNextBlockActivity();
            }
        }
    }

    public itemSelected(item: CompactListItem) {
        this._router.navigate(['', { outlets: { [this.drillInOutlet]: ['activity', item.id] }}]);
    }

    private _updateItems() {
        if (this.vehicleBlockActivities && this.vehicleBlockActivities.length > 0) {
            this.items = _.map(this.vehicleBlockActivities, va => {
                const item = new CompactListItem();
                item.id = va.vehicleActivityId;
                item.highlighted = this._isBlockHighlighted(this.vehicle, va);
                item.properties['time'] = `${va.startTime} - ${va.endTime}`;
                item.properties['routeNumber'] = va?.routeName;
                item.properties['tripNumber'] = va.tripNumber;
                item.properties['description'] = va.tripDescription;
                item.properties['activityType'] = VehicleActivityTypeHelper.getString(va.type);
                item.properties['activityTypeColor'] = VehicleActivityTypeHelper.getColor(va.type);
                return item;
            });

            if (this.vehicle.tripNumber) {
                const indexOfHighlightedRow = _.indexOf(this.items, _.find(this.items, it => it.highlighted));
                if (indexOfHighlightedRow > 0) {
                    _.map(this.items.slice(0, indexOfHighlightedRow), it => it.muted = true);
                }
            } else { // vehicle is in tom, layovr or something else
                const _estimatedItem = _.find(this.items, item => item.id === this._getEstimatedBlockActivity()?.vehicleActivityId);
                if (_estimatedItem) {
                    _estimatedItem.semiHighlighted = true;
                }
            }
        } else if (this.vehicleBlockActivities && this.vehicleBlockActivities.length === 0) {
            this.items = [];
        } else {
            this.items = undefined;
        }
    }

    private _isBlockHighlighted(vehicle: Vehicle, vehicleBlockActivity: VehicleBlockActivityInfo): boolean {
        return vehicle.tripNumber
            && vehicle.tripNumber?.toString() === vehicleBlockActivity.tripNumber?.toString()
            && vehicle.routeNumber?.toString() === vehicleBlockActivity.routeName?.toString();
    }

    private _showOnlyCurrentAndNextBlockActivity() {
        const indexOfHighlightedRow =
            _.indexOf(this.items, _.find(this.items, it => it.highlighted));
        const indexOfSemiHighlighted = _.indexOf(this.items, _.find(this.items, it => it.semiHighlighted));

        const indexOfCurrent = indexOfHighlightedRow > -1
            ? indexOfHighlightedRow
            : indexOfSemiHighlighted > -1 ? indexOfSemiHighlighted : null;

        if (indexOfCurrent < 0 || indexOfCurrent == undefined) {
            this.items = [];
            return;
        }

        this.items = this.items.slice(indexOfCurrent, indexOfCurrent + 2);
    }

    private _getEstimatedBlockActivity(): VehicleBlockActivityInfo {
        if (!this.vehicleBlockActivities || this.vehicle.tripNumber) {
            return null;
        }

        const now = moment();

        // Find closest block by starttime and endtime
        const itemInTimeFrame = _.find(this.vehicleBlockActivities, va => {
            const startTimeAsMoment = moment(va.startDateTime);
            const endTimeAsMoment = moment(va.endDateTime);
            return now.isAfter(startTimeAsMoment) && now.isBefore(endTimeAsMoment);
        });

        if (itemInTimeFrame) {
            return itemInTimeFrame;
        }

        // Find closest block by only starttime
        const allStartedBlockActivities = _.filter(this.vehicleBlockActivities, va => {
            const startTimeAsMoment = moment(va.startDateTime);
            return startTimeAsMoment.isAfter(now);
        });

        const itemWithStartClosestToNow = _.first(allStartedBlockActivities);
        if (itemWithStartClosestToNow) {
            return itemWithStartClosestToNow;
        }

        return null;
    }

    private _selectItem(id: string) {
        if (!this.items) {
            return;
        }

        const found = _.find(this.items, item => item.id === id);
        if (found) {
            found.selected = true;
        }
    }

    private _resetSelection() {
        // reset selection
        const selectedItems = _.chain(this.items)
            .filter(item => item.selected)
            .value();
        _.each(selectedItems, item => item.selected = false);
    }

    private _initColumnDefaults() {
        this.columns = [
            ColumnFactory.getRouteNumberColumn({
                width: 26,
            }),
            ColumnFactory.getTripNumberColumn(),
            ColumnFactory.getActivityTime(),
            ColumnFactory.getActivityDescription({
                icon: undefined
            }),
            ColumnFactory.getActivityType({
                icon: undefined
            })
        ];
    }

}
